/* eslint-disable no-null/no-null */
import type { TeactNode } from '../../../../lib/teact/teact';
import React, {
  memo, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from '../../../../lib/teact/teact';
import { setExtraStyles } from '../../../../lib/teact/teact-dom';
import { getActions, withGlobal } from '../../../../global';

import type {
  ApiActivity,
  ApiBaseCurrency,
  ApiChain,
  ApiCurrencyRates,
  ApiNft,
  ApiStakingState,
  ApiSwapAsset,
  ApiTokenWithPrice,
  ApiWalletTransactionCategory,
  ApiWalletTransactionCounterparty,
  ApiWalletTransactionMetadata,
} from '../../../../api/types';
import type { Account, SavedAddress, Theme } from '../../../../global/types';
import type { ActivityHistoryFilters as HistoryFilters } from './activityHistoryFilters';
import { ContentTab } from '../../../../global/types';

import {
  ANIMATED_STICKER_BIG_SIZE_PX,
  PORTRAIT_MIN_ASSETS_TAB_VIEW,
} from '../../../../config';
import { requestMeasure } from '../../../../lib/fasterdom/fasterdom';
import { forceMeasure } from '../../../../lib/fasterdom/stricterdom';
import { getIsTinyOrScamTransaction } from '../../../../global/helpers';
import {
  selectAccount,
  selectAccounts,
  selectAccountStakingStatesBySlug,
  selectActivityHistoryIds,
  selectCurrentAccountId,
  selectCurrentAccountSettings,
  selectCurrentAccountState,
  selectIsHistoryEndReached,
} from '../../../../global/selectors';
import { getIsHiddenNftActivity, parseTxId } from '../../../../util/activities';
import buildClassName from '../../../../util/buildClassName';
import { formatHumanDay, getDayStartAt } from '../../../../util/dateFormat';
import generateUniqueId from '../../../../util/generateUniqueId';
import { getLocalAddressName } from '../../../../util/getLocalAddressName';
import { isKeyCountGreater } from '../../../../util/isEmptyObject';
import { getIsTransactionWithPoisoning } from '../../../../util/poisoningHash';
import { buildAddressCounterpartyIndex } from '../../../../util/walletMetadataDirectory';
import {
  getActivityWalletSystemMetadata,
  isWalletSystemCategory,
  isWalletSystemCounterparty,
} from '../../../../util/walletSystemMetadata';
import { REM } from '../../../../util/windowEnvironment';
import { ANIMATED_STICKERS_PATHS } from '../../../ui/helpers/animatedAssets';
import {
  getScrollableContainer,
  getScrollContainerClosestSelector,
} from '../../helpers/scrollableContainer';
import {
  createActivityHistoryFilter, DEFAULT_HISTORY_FILTERS, getActivityHistorySearchText,
  hasActivityHistoryFilters,
} from './activityHistoryFilters';

import useAppTheme from '../../../../hooks/useAppTheme';
import useDebouncedValue from '../../../../hooks/useDebouncedValue';
import { getIsPortrait, useDeviceScreen } from '../../../../hooks/useDeviceScreen';
import useInfiniteScroll from '../../../../hooks/useInfiniteScroll';
import useLang from '../../../../hooks/useLang';
import useLastCallback from '../../../../hooks/useLastCallback';
import useLayoutEffectWithPrevDeps from '../../../../hooks/useLayoutEffectWithPrevDeps';
import usePrevious from '../../../../hooks/usePrevious';
import useSyncEffect from '../../../../hooks/useSyncEffect';
import useSyncEffectWithPrevDeps from '../../../../hooks/useSyncEffectWithPrevDeps';
import useUpdateIndicator from '../../../../hooks/useUpdateIndicator';
import useWalletMetadataDirectory from '../../../../hooks/useWalletMetadataDirectory';
import useActivityMetadata from './hooks/useActivityMetadata';

import AnimatedIconWithPreview from '../../../ui/AnimatedIconWithPreview';
import Button from '../../../ui/Button';
import InfiniteScroll from '../../../ui/InfiniteScroll';
import LoadingDots from '../../../ui/LoadingDots';
import Spinner from '../../../ui/Spinner';
import Transition from '../../../ui/Transition';
import Activity, { getActivityHeight } from './Activity';
import ActivityHistoryFilters from './ActivityHistoryFilters';
import ActivityListItem from './ActivityListItem';

import styles from './Activities.module.scss';
import overviewStyles from './OverviewCell.module.scss';

interface OwnProps {
  isActive?: boolean;
  totalTokensAmount?: number;
  scrollContainerSelector?: string;
  isWidget?: boolean;
  isPreview?: boolean;
  isHistory?: boolean;
  withPreviewHeading?: boolean;
  historyCategory?: string;
  historyControls?: TeactNode;
  onHistoryCategoryChange?: (category: string) => void;
  onHistoryCategoriesChange?: (categories: ApiWalletTransactionCategory[]) => void;
  onScroll?: (e: React.UIEvent<HTMLElement>) => void;
}

type StateProps = {
  currentAccountId: string;
  slug?: string;
  accountChains?: Partial<Record<ApiChain, unknown>>;
  areTinyTransfersHidden?: boolean;
  areUnverifiedNftsHidden?: boolean;
  byId?: Record<string, ApiActivity>;
  allActivityIds?: string[];
  tokensBySlug: Record<string, ApiTokenWithPrice>;
  swapTokensBySlug?: Record<string, ApiSwapAsset>;
  currentActivityId?: string;
  activityIdReplacements?: Record<string, string>;
  savedAddresses?: SavedAddress[];
  isHistoryEndReached: boolean;
  alwaysShownSlugs?: string[];
  theme: Theme;
  baseCurrency: ApiBaseCurrency;
  currencyRates: ApiCurrencyRates;
  isSensitiveDataHidden?: true;
  stakingStateBySlug: Record<string, ApiStakingState>;
  nftsByAddress?: Record<string, ApiNft>;
  blacklistedNftAddresses?: string[];
  whitelistedNftAddresses?: string[];
  accounts?: Record<string, Account>;
};

interface ItemPosition {
  /** In rem */
  top: number;
  /** In rem */
  height: number;
}

const FURTHER_SLICE = 30;
const LOAD_RETRY_INTERVAL = 10000; // 10 sec

const LIST_TOP_PADDING = 0.5; // rem
const DATE_HEADER_HEIGHT = 2.125; // rem

// After this threshold, the animations of adding new activities (controlled by `useActivityOrderDiff`) won't be visible
// to the user, so the animation is disabled to improve the performance. An important part that makes the additions
// invisible is the `usePreventScrolledListShift` hook.
const SCROLL_THRESHOLD = (LIST_TOP_PADDING + DATE_HEADER_HEIGHT) * REM;

const DATE_ID_PREFIX = 'date:';

const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_DICTIONARY = Object.freeze({});
const TOP_SPACE = 1;
const DEFAULT_PREVIEW_LIMIT = 5;
const PREVIEW_HEIGHT_TOLERANCE_PX = 1;

type ResolvedActivityMetadata = Omit<ApiWalletTransactionMetadata, 'counterparty' | 'category'> & {
  counterparty: ApiWalletTransactionCounterparty | null;
  category: ApiWalletTransactionCategory | null;
};

export function getAdaptivePreviewLimit(
  activityIds: string[],
  byId: Record<string, ApiActivity> | undefined,
  availableHeight: number,
  rem = REM,
) {
  if (
    !activityIds.length
    || !byId
    || !Number.isFinite(availableHeight)
    || availableHeight <= 0
    || !Number.isFinite(rem)
    || rem <= 0
  ) {
    return DEFAULT_PREVIEW_LIMIT;
  }

  let occupiedHeight = 0;
  let limit = 0;

  for (const activityId of activityIds.slice(0, FURTHER_SLICE)) {
    const activity = byId[activityId];
    if (!activity) return DEFAULT_PREVIEW_LIMIT;

    const itemHeight = getActivityHeight(activity) * rem;
    if (occupiedHeight + itemHeight > availableHeight + PREVIEW_HEIGHT_TOLERANCE_PX) {
      return Math.max(1, limit);
    }

    occupiedHeight += itemHeight;
    limit += 1;
  }

  return Math.max(1, limit);
}

export function Activities({
  isActive,
  scrollContainerSelector,
  currentAccountId,
  accountChains = EMPTY_DICTIONARY,
  slug,
  byId,
  allActivityIds,
  tokensBySlug,
  swapTokensBySlug,
  areTinyTransfersHidden,
  areUnverifiedNftsHidden,
  currentActivityId,
  activityIdReplacements,
  savedAddresses,
  isHistoryEndReached,
  alwaysShownSlugs,
  theme,
  baseCurrency,
  currencyRates,
  isSensitiveDataHidden,
  stakingStateBySlug,
  nftsByAddress,
  blacklistedNftAddresses,
  whitelistedNftAddresses,
  accounts,
  onScroll,
  isPreview,
  isHistory,
  withPreviewHeading = true,
  historyCategory,
  historyControls,
  onHistoryCategoryChange,
  onHistoryCategoriesChange,
}: Omit<OwnProps, 'totalTokensAmount'> & StateProps) {
  const { fetchPastActivities, selectToken, setActiveContentTab, showActivityInfo } = getActions();

  const lang = useLang();
  const { isLandscape, isPortrait } = useDeviceScreen();

  const resolvedScrollSelector = scrollContainerSelector
    ?? getScrollContainerClosestSelector(isActive, isPortrait);

  const containerRef = useRef<HTMLDivElement>();
  const historyFiltersRef = useRef<HTMLDivElement>();
  const previewRef = useRef<HTMLElement>();
  const previewGroupRef = useRef<HTMLDivElement>();
  const isUpdating = useUpdateIndicator('activitiesUpdateStartedAt');
  const [previewLimit, setPreviewLimit] = useState(DEFAULT_PREVIEW_LIMIT);

  const appTheme = useAppTheme(theme);
  const hasUserScrolledRef = useRef(false);
  const [localFilters, setLocalFilters] = useState(DEFAULT_HISTORY_FILTERS);
  const filters = useMemo(() => historyCategory === undefined
    ? localFilters
    : { ...localFilters, category: historyCategory }, [historyCategory, localFilters]);
  const handleFiltersChange = useLastCallback((nextFilters: HistoryFilters) => {
    setLocalFilters(nextFilters);
    if (historyCategory !== undefined) onHistoryCategoryChange?.(nextFilters.category ?? '');
  });
  const debouncedQuery = useDebouncedValue(filters.query, 150);
  const query = filters.query.trim() ? debouncedQuery : '';
  const appliedFilters = useMemo(() => ({
    query, direction: filters.direction, status: filters.status, asset: filters.asset,
    counterparty: filters.counterparty, fromDate: filters.fromDate, toDate: filters.toDate,
    category: filters.category,
  }), [query, filters.direction, filters.status, filters.asset, filters.counterparty,
    filters.fromDate, filters.toDate, filters.category]);
  const hasFilters = Boolean(isHistory && hasActivityHistoryFilters(appliedFilters));
  useSyncEffect(() => {
    if (isHistory) hasUserScrolledRef.current = false;
  }, [appliedFilters, isHistory]);

  // As with `allActivityIds`, `undefined` means "not loaded yet" and `[]` means "no activities"
  const baseActivityIds = useMemo(() => filterActivityIds(
    allActivityIds ?? EMPTY_ARRAY,
    byId ?? EMPTY_DICTIONARY,
    slug,
    tokensBySlug,
    areTinyTransfersHidden,
    alwaysShownSlugs,
    blacklistedNftAddresses,
    whitelistedNftAddresses,
    areUnverifiedNftsHidden,
  ), [areTinyTransfersHidden, byId, alwaysShownSlugs, allActivityIds, slug, tokensBySlug,
    blacklistedNftAddresses, whitelistedNftAddresses, areUnverifiedNftsHidden]);

  const transactionHashes = useMemo(() => {
    const ids = isPreview ? baseActivityIds.slice(0, previewLimit) : baseActivityIds;
    return [...new Set(ids.flatMap((id) => {
      const activity = byId?.[id];
      if (activity?.kind !== 'transaction' || tokensBySlug[activity.slug]?.chain !== 'tron') return [];
      const hash = parseTxId(activity.id).hash;
      return /^[a-f\d]{64}$/i.test(hash) ? [hash] : [];
    }))];
  }, [baseActivityIds, byId, isPreview, previewLimit, tokensBySlug]);
  const metadataByHash = useActivityMetadata(
    currentAccountId, transactionHashes, isActive, currentActivityId, accounts?.[currentAccountId]?.type === 'view',
  );
  const directory = useWalletMetadataDirectory(currentAccountId, accounts,
    Boolean(isActive && accounts?.[currentAccountId]?.type !== 'view' && accounts?.[currentAccountId]?.byChain.tron));
  const counterpartiesByAddress = useMemo(() => buildAddressCounterpartyIndex(directory.counterparties,
    accounts?.[currentAccountId]?.byChain.tron?.address), [directory.counterparties, accounts, currentAccountId]);
  const categoriesById = useMemo(() => new Map(directory.categories.map((category) => [category.id, category])),
    [directory.categories]);
  const metadataByActivityId = useMemo<Record<string, ResolvedActivityMetadata>>(() => Object.fromEntries(
    baseActivityIds.flatMap((id) => {
      const activity = byId![id];
      if (activity.kind !== 'transaction' || tokensBySlug[activity.slug]?.chain !== 'tron') return [];
      const hash = parseTxId(id).hash;
      const peerAddress = activity.isIncoming ? activity.fromAddress : activity.toAddress;
      const counterparty = counterpartiesByAddress.get(peerAddress ?? '');
      const systemMetadata = getActivityWalletSystemMetadata(activity, lang);
      return [[id, {
        ...metadataByHash[hash], transaction_hash: hash, attachments: metadataByHash[hash]?.attachments ?? [],
        updated_at: metadataByHash[hash]?.updated_at ?? '',
        counterparty: counterparty ?? systemMetadata?.counterparty ?? null,
        category: categoriesById.get(metadataByHash[hash]?.category?.id ?? '') ?? systemMetadata?.category ?? null,
      } satisfies ResolvedActivityMetadata]];
    })), [baseActivityIds, byId, tokensBySlug, metadataByHash, counterpartiesByAddress, categoriesById, lang]);
  const visibleCounterparties = useMemo(() => {
    const records = directory.counterparties.filter((record) => record.addresses?.length);
    const systemRecord = Object.values(metadataByActivityId)
      .map(({ counterparty }) => counterparty)
      .find((counterparty) => counterparty && isWalletSystemCounterparty(counterparty));
    if (systemRecord && !records.some(({ id }) => id === systemRecord.id)) records.push(systemRecord);
    return records.sort((first, second) => first.name.localeCompare(second.name));
  }, [directory.counterparties, metadataByActivityId]);
  const categories = useMemo(() => {
    const records = [...directory.categories];
    Object.values(metadataByActivityId).forEach(({ category }) => {
      if (category && isWalletSystemCategory(category) && !records.some(({ id }) => id === category.id)) {
        records.push(category);
      }
    });
    return records;
  }, [directory.categories, metadataByActivityId]);
  useEffect(() => {
    if (isHistory) onHistoryCategoriesChange?.(categories);
  }, [categories, isHistory, onHistoryCategoriesChange]);

  const historyTokens = useMemo(() => {
    const slugs = new Set(baseActivityIds.flatMap((id) => {
      const activity = byId![id];
      return activity.kind === 'transaction' ? [activity.slug] : [activity.from, activity.to];
    }));
    return [...slugs].map((tokenSlug) => tokensBySlug[tokenSlug]).filter(Boolean)
      .sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [baseActivityIds, byId, tokensBySlug]);

  const shouldIndexSearch = Boolean(isHistory && query.trim());
  const searchTexts = useMemo(() => {
    if (!shouldIndexSearch) return undefined;
    const addressNames = new Map<string, string | undefined>();
    return Object.fromEntries(baseActivityIds.map((id) => {
      const activity = byId![id];
      const metadata = metadataByActivityId[id];
      const token = activity.kind === 'transaction' ? tokensBySlug[activity.slug] : undefined;
      let addressName: string | undefined;
      if (activity.kind === 'transaction' && token) {
        const address = activity.isIncoming ? activity.fromAddress : activity.toAddress;
        const key = `${token.chain}:${address}`;
        if (!addressNames.has(key)) {
          addressNames.set(key, getLocalAddressName({ address, chain: token.chain,
            currentAccountId, accounts, savedAddresses }));
        }
        addressName = addressNames.get(key);
      }
      return [id, getActivityHistorySearchText(activity, tokensBySlug, metadata, addressName)];
    }));
  }, [shouldIndexSearch, baseActivityIds, byId, metadataByActivityId, tokensBySlug,
    currentAccountId, accounts, savedAddresses]);
  const matchesFilters = useMemo(() => createActivityHistoryFilter(appliedFilters, tokensBySlug),
    [appliedFilters, tokensBySlug]);

  const filteredActivityIds = useMemo(() => hasFilters ? baseActivityIds.filter((id) => {
    const activity = byId![id];
    const metadata = metadataByActivityId[id];
    return matchesFilters(activity, metadata, undefined, searchTexts?.[id]);
  }) : baseActivityIds,
  [baseActivityIds, byId, metadataByActivityId, hasFilters, matchesFilters, searchTexts]);
  const visibleActivityIds = useMemo(() => (
    isPreview ? filteredActivityIds.slice(0, previewLimit) : filteredActivityIds
  ), [filteredActivityIds, isPreview, previewLimit]);

  const listItemIds = useMemo(() => {
    if (!visibleActivityIds.length && !isHistoryEndReached) {
      // When all the activities are hidden, the further activities are being loaded, so the spinner should be shown.
      return undefined;
    }

    return isPreview ? visibleActivityIds : addDatesToActivityIds(visibleActivityIds, byId);
  }, [visibleActivityIds, isPreview, byId, isHistoryEndReached]);

  const firstListItemId = listItemIds?.[0];

  useLayoutEffect(() => {
    if (!isPreview || !withPreviewHeading || !isActive) {
      setPreviewLimit(DEFAULT_PREVIEW_LIMIT);
      return undefined;
    }

    const section = previewRef.current;
    const group = previewGroupRef.current;
    if (!section || !group) return undefined;

    const scrollContainer = (resolvedScrollSelector
      ? section.closest<HTMLElement>(resolvedScrollSelector)
      : undefined) ?? document.scrollingElement as HTMLElement | null;
    if (!scrollContainer) return undefined;

    let isDisposed = false;
    const updateLimit = () => {
      const currentSection = previewRef.current;
      const currentGroup = previewGroupRef.current;
      if (isDisposed || !currentSection || !currentGroup || !currentSection.isConnected) return;

      const sectionRect = currentSection.getBoundingClientRect();
      const groupRect = currentGroup.getBoundingClientRect();
      const scrollRect = scrollContainer.getBoundingClientRect();
      const sectionTop = sectionRect.top - scrollRect.top + scrollContainer.scrollTop;
      const groupOffset = groupRect.top - sectionRect.top;

      let trailingPadding = 0;
      let ancestor = currentSection.parentElement;
      while (ancestor && ancestor !== scrollContainer) {
        trailingPadding += parseFloat(getComputedStyle(ancestor).paddingBottom) || 0;
        ancestor = ancestor.parentElement;
      }

      const availableHeight = scrollContainer.clientHeight - sectionTop - groupOffset - trailingPadding;
      const nextLimit = getAdaptivePreviewLimit(filteredActivityIds, byId, availableHeight);
      setPreviewLimit((currentLimit) => currentLimit === nextLimit ? currentLimit : nextLimit);
    };
    const scheduleUpdate = () => requestMeasure(updateLimit);

    forceMeasure(updateLimit);

    const resizeObserver = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(scheduleUpdate);
    if (resizeObserver) {
      let ancestor: HTMLElement | null = section;
      while (ancestor) {
        resizeObserver.observe(ancestor);
        if (ancestor === scrollContainer) break;
        ancestor = ancestor.parentElement;
      }
    }
    window.addEventListener('resize', scheduleUpdate);
    window.visualViewport?.addEventListener('resize', scheduleUpdate);

    return () => {
      isDisposed = true;
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      window.visualViewport?.removeEventListener('resize', scheduleUpdate);
    };
  }, [byId, filteredActivityIds, isActive, isPreview, resolvedScrollSelector, withPreviewHeading]);

  const loadMore = useLastCallback(() => {
    if (!isActive || isHistoryEndReached || (isPreview && baseActivityIds.length >= previewLimit)) return;
    fetchPastActivities({ accountId: currentAccountId, slug, shouldLoadWithBudget: true });
  });

  const [viewportIds, getMore, resetScroll] = useInfiniteScroll({
    loadMoreBackwards: loadMore,
    listIds: listItemIds,
    listSlice: FURTHER_SLICE,
    slug: isHistory ? JSON.stringify(appliedFilters) : slug,
    isActive,
    isDisabled: isPreview,
  });

  useEffect(() => {
    if (!isHistory) return;
    let isCancelled = false;
    const container = historyFiltersRef.current?.closest<HTMLElement>('.activity-history-scroll');
    if (container) {
      requestMeasure(() => {
        if (isCancelled || !container.isConnected) return;
        container.scrollTo({ top: 0, behavior: 'instant' });
      });
    }
    hasUserScrolledRef.current = false;
    return () => {
      isCancelled = true;
    };
  }, [appliedFilters, isHistory]);

  const getListItemKey = useListItemKeys(viewportIds ?? EMPTY_ARRAY, activityIdReplacements);

  // The move animation should remain only for the case when new transactions appear in the feed.
  // At the same time, the smooth feed appearance animation should be kept when the loading is completed.
  const prevSlug = usePrevious(slug);
  const prevIsLoading = usePrevious(listItemIds === undefined);
  const shouldUseAnimations = Boolean(isActive && !hasUserScrolledRef.current && (prevIsLoading || prevSlug === slug));

  const itemPositionById = useMemo(() => {
    const itemPositionById: Record<string, ItemPosition> = {};
    let top = 0;

    listItemIds?.forEach((itemId, index) => {
      const height = itemId.startsWith(DATE_ID_PREFIX)
        ? DATE_HEADER_HEIGHT + (index === 0 ? LIST_TOP_PADDING : 0)
        : getActivityHeight(byId![itemId]);

      itemPositionById[itemId] = { top, height };
      top += height;
    });

    return itemPositionById;
  }, [byId, listItemIds]);

  const currentContainerHeight = useMemo(
    () => {
      if (!viewportIds?.length) return 0;

      const lastViewportId = viewportIds[viewportIds.length - 1];
      const activityOffset = itemPositionById[lastViewportId];

      return activityOffset ? activityOffset.top + activityOffset.height : 0;
    },
    [itemPositionById, viewportIds],
  );

  usePreventScrolledListShift(
    viewportIds ?? EMPTY_ARRAY,
    itemPositionById,
    isPortrait,
    containerRef,
    hasUserScrolledRef,
    scrollContainerSelector,
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || isPreview || scrollContainerSelector) return;

    setExtraStyles(container, { height: isLandscape ? '' : `${currentContainerHeight + TOP_SPACE}rem` });
  }, [isLandscape, currentContainerHeight, scrollContainerSelector, isPreview]);

  // Requests the history when the UI shows a spinner instead of the list.
  // Keeps requesting the history when all the currently loaded activities are hidden.
  // Retries on an interval, because a failed request finishes silently with no state change -
  // without a retry the spinner would stay forever (nothing else re-triggers the load).
  useEffect(() => {
    if (!isActive || listItemIds?.length || isHistoryEndReached) {
      return undefined;
    }

    loadMore();
    const intervalId = window.setInterval(loadMore, LOAD_RETRY_INTERVAL);

    return () => window.clearInterval(intervalId);
  }, [slug, allActivityIds, listItemIds, isHistoryEndReached, loadMore, isActive]);

  // Reset scroll and scroll tracking when the tab becomes inactive
  useEffect(() => {
    if (!isActive && !isLandscape) {
      resetScroll?.();
      hasUserScrolledRef.current = false;
    }
  }, [isActive, isLandscape, resetScroll]);

  const handleScroll = useLastCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = (e.target as HTMLDivElement).scrollTop;

    hasUserScrolledRef.current = scrollTop >= SCROLL_THRESHOLD;

    onScroll?.(e);
  });

  const handleActivityClick = useLastCallback((id: string) => {
    showActivityInfo({ id });
  });
  const openHistory = useLastCallback(() => {
    selectToken({ slug: undefined }, { forceOnHeavyAnimation: true });
    setActiveContentTab({ tab: ContentTab.History });
  });

  if (!currentAccountId) {
    return undefined;
  }

  function renderDate(dateValue: number, isFirst?: boolean) {
    const formattedDate = formatHumanDay(lang, dateValue);
    const date = <div className={styles.date}>{formattedDate}</div>;

    if (!isFirst) {
      return date;
    }

    const dateWithLoader = (
      <div className={buildClassName(styles.date, styles.date_withLoadingDots)}>
        {formattedDate}
        <LoadingDots isActive isDoubled className={styles.loadingDots} />
      </div>
    );

    return (
      <Transition
        name="semiFade"
        activeKey={isUpdating ? 1 : 0}
        className={styles.dateContainer}
        slideClassName={styles.dateSlide}
      >
        {isUpdating ? dateWithLoader : date}
      </Transition>
    );
  }

  function renderActivity(id: string, isLast: boolean) {
    const activity = byId?.[id];
    if (!activity) return undefined;
    const isMultichainAccount = isKeyCountGreater(accountChains, 1);
    return (
      <Activity
        activity={activity}
        isLast={isLast}
        isActive={activity.id === currentActivityId}
        tokensBySlug={tokensBySlug}
        swapTokensBySlug={swapTokensBySlug}
        appTheme={appTheme}
        isSensitiveDataHidden={isSensitiveDataHidden}
        nftsByAddress={nftsByAddress}
        currentAccountId={currentAccountId}
        stakingStateBySlug={stakingStateBySlug}
        savedAddresses={savedAddresses}
        withChainIcon={isMultichainAccount}
        accounts={accounts}
        baseCurrency={baseCurrency}
        currencyRates={currencyRates}
        counterparty={activity.kind === 'transaction' ? metadataByActivityId[id]?.counterparty ?? undefined : undefined}
        category={isHistory && activity.kind === 'transaction'
          ? metadataByActivityId[id]?.category ?? undefined
          : undefined}
        onClick={handleActivityClick}
      />
    );
  }

  function renderHistory() {
    if (!viewportIds) return undefined;

    return viewportIds.map((itemId, index) => {
      const topOffset = itemPositionById[itemId]?.top ?? 0;
      let itemContent: TeactNode;

      if (itemId.startsWith(DATE_ID_PREFIX)) {
        itemContent = renderDate(
          Number(itemId.slice(DATE_ID_PREFIX.length)),
          itemId === firstListItemId,
        );
      } else {
        const isLastInDay = index === viewportIds.length - 1 || viewportIds[index + 1].startsWith(DATE_ID_PREFIX);
        itemContent = renderActivity(itemId, isLastInDay);
      }

      return (
        <ActivityListItem key={getListItemKey(itemId)} topOffset={topOffset} withAnimation={shouldUseAnimations}>
          {itemContent}
        </ActivityListItem>
      );
    });
  }

  function renderActivityList() {
    if (listItemIds === undefined) {
      return (
        <div
          className={buildClassName(styles.emptyList, styles.emptyListLoading,
            isHistory && styles.historyListGroup)}
        >
          <Spinner />
        </div>
      );
    }

    if (!listItemIds.length) {
      return (
        <div className={buildClassName(styles.emptyList, isHistory && styles.historyListGroup)}>
          <AnimatedIconWithPreview
            play={isActive}
            tgsUrl={ANIMATED_STICKERS_PATHS.noData}
            previewUrl={ANIMATED_STICKERS_PATHS.noDataPreview}
            size={ANIMATED_STICKER_BIG_SIZE_PX}
            className={styles.sticker}
            noLoop={false}
            nonInteractive
          />
          <p className={styles.emptyListTitle}>{lang(hasFilters ? '$history_no_matches' : 'No Activity')}</p>
        </div>
      );
    }

    return (
      <InfiniteScroll
        ref={containerRef}
        className={buildClassName(
          'custom-scroll',
          styles.listGroup,
          isHistory && styles.historyListGroup,
          scrollContainerSelector && styles.listGroupExternalScroll,
        )}
        scrollContainerClosest={resolvedScrollSelector}
        items={viewportIds}
        preloadBackwards={FURTHER_SLICE}
        withAbsolutePositioning
        maxHeight={`${currentContainerHeight}rem`}
        onLoadMore={getMore}
        onScroll={handleScroll}
      >
        {renderHistory()}
      </InfiniteScroll>
    );
  }

  if (isPreview && listItemIds !== undefined) {
    return (
      <section ref={previewRef} className={styles.preview}>
        {withPreviewHeading && (
          <div className={styles.previewHeadingRow}>
            <h3 className={buildClassName(overviewStyles.caption, styles.previewHeading)}>{lang('Activity')}</h3>
            <Button isSimple className={styles.previewHeaderAction} onClick={openHistory}>
              {lang('Show All')}
              <i className="icon-chevron-right" aria-hidden />
            </Button>
          </div>
        )}
        <div ref={previewGroupRef} className={styles.previewGroup}>
          {listItemIds.length ? (
            <>
              {listItemIds.map((id, index) => (
                <div key={id}>{renderActivity(id, index === listItemIds.length - 1)}</div>
              ))}
              {!withPreviewHeading && (
                <Button
                  isSimple
                  className={buildClassName(overviewStyles.showAll, styles.previewShowAll)}
                  onClick={openHistory}
                >
                  <i className={buildClassName(overviewStyles.showAllIcon, 'icon-menu-list')} aria-hidden />
                  {lang('$history_show_all')}
                  <i className={buildClassName(styles.previewChevron, 'icon-chevron-right')} aria-hidden />
                </Button>
              )}
            </>
          ) : renderActivityList()}
        </div>
      </section>
    );
  }
  return (
    <>
      {isHistory && (
        <>
          <ActivityHistoryFilters
            containerRef={historyFiltersRef}
            filters={filters}
            tokens={historyTokens}
            counterparties={visibleCounterparties}
            onChange={handleFiltersChange}
          />
          {historyControls}
        </>
      )}
      {renderActivityList()}
    </>
  );
}

export default memo(
  withGlobal<OwnProps>(
    (global, { isWidget }): StateProps => {
      const currentAccountId = selectCurrentAccountId(global)!;
      const accountState = selectCurrentAccountState(global);
      const accountSettings = selectCurrentAccountSettings(global);
      const slug = isWidget ? undefined : accountState?.currentTokenSlug;
      const stakingStateBySlug = selectAccountStakingStatesBySlug(global, currentAccountId);
      const { activities } = accountState || {};
      const { byId } = activities || {};
      const { byAddress } = accountState?.nfts || {};
      const accounts = selectAccounts(global);
      const isHistoryEndReached = selectIsHistoryEndReached(global, currentAccountId, slug);
      const allActivityIds = selectActivityHistoryIds(global, currentAccountId, slug);

      return {
        accountChains: selectAccount(global, currentAccountId)?.byChain,
        currentAccountId,
        slug,
        byId,
        allActivityIds,
        tokensBySlug: global.tokenInfo.bySlug,
        swapTokensBySlug: global.swapTokenInfo?.bySlug,
        areTinyTransfersHidden: global.settings.areTinyTransfersHidden,
        areUnverifiedNftsHidden: global.settings.areUnverifiedNftsHidden,
        savedAddresses: accountState?.savedAddresses,
        isHistoryEndReached,
        currentActivityId: accountState?.currentActivityId,
        activityIdReplacements: activities?.activityIdReplacements,
        alwaysShownSlugs: accountSettings?.alwaysShownSlugs,
        theme: global.settings.theme,
        baseCurrency: global.settings.baseCurrency,
        currencyRates: global.currencyRates,
        stakingStateBySlug,
        isSensitiveDataHidden: global.settings.isSensitiveDataHidden,
        nftsByAddress: byAddress,
        blacklistedNftAddresses: accountState?.blacklistedNftAddresses,
        whitelistedNftAddresses: accountState?.whitelistedNftAddresses,
        accounts,
      };
    },
    (global, { totalTokensAmount, isWidget }, stickToFirst) => {
      const accountState = selectCurrentAccountState(global);
      const shouldShowSeparateAssetsPanel = getIsPortrait()
        && totalTokensAmount !== undefined
        && totalTokensAmount <= PORTRAIT_MIN_ASSETS_TAB_VIEW;

      return stickToFirst((
        isWidget
        // The token screen shows activities no matter which wallet tab is open.
        || Boolean(accountState?.currentTokenSlug)
        || accountState?.activeContentTab === ContentTab.Activity
        || (accountState?.activeContentTab === ContentTab.Assets && shouldShowSeparateAssetsPanel)
      ) && selectCurrentAccountId(global));
    },
  )(Activities),
);

function filterActivityIds(
  allActivityIds: readonly string[],
  byId: Record<string, ApiActivity>,
  slug: string | undefined,
  tokensBySlug: Record<string, ApiTokenWithPrice>,
  areTinyTransfersHidden?: boolean,
  alwaysShownSlugs?: string[],
  blacklistedNftAddresses?: string[],
  whitelistedNftAddresses?: string[],
  areUnverifiedNftsHidden?: boolean,
) {
  return allActivityIds.filter((id) => {
    const activity = byId?.[id];
    if (!activity) return false;

    if (activity?.shouldHide) return false;

    if (activity?.kind === 'swap') {
      return !slug || activity.from === slug || activity.to === slug;
    } else {
      return activity?.slug
        && (!slug || activity.slug === slug)
        && (
          !areTinyTransfersHidden
          || (slug && tokensBySlug[activity.slug]?.priceUsd === 0)
          || !getIsTinyOrScamTransaction(activity, tokensBySlug[activity.slug])
          || alwaysShownSlugs?.includes(activity.slug)
        )
        && !getIsHiddenNftActivity(
          activity, blacklistedNftAddresses, whitelistedNftAddresses, areUnverifiedNftsHidden,
        )
        && !getIsTransactionWithPoisoning(activity);
    }
  });
}

function addDatesToActivityIds(activityIds: readonly string[], byId: Record<string, ApiActivity> = {}) {
  let lastActivityDayStart = Infinity;
  const listItemIds: string[] = [];

  // Mix-in the date headers to the list of activity ids
  for (const activityId of activityIds) {
    const activityDayStart = getDayStartAt(byId[activityId].timestamp);

    // The clause is not `activityDayStart !== lastActivityDayStart`, because the days can go backwards, which will
    // cause duplicated date Teact keys. The days can go backwards because the pending activities are on the top
    // regardless of their timestamp.
    const isNewDay = activityDayStart < lastActivityDayStart;

    if (isNewDay) {
      lastActivityDayStart = activityDayStart;
      listItemIds.push(`${DATE_ID_PREFIX}${activityDayStart}`);
    }
    listItemIds.push(activityId);
  }

  return listItemIds;
}

/**
 * Pending activity ids may change. If the ids are used as the list keys, excessive blinking animations occur.
 * This hook creates stable keys for the list items, which prevents the excessive animations.
 */
function useListItemKeys(
  viewportIds: readonly string[],
  activityIdReplacements: Record<string, string> | undefined,
) {
  const keyByIdRef = useRef<Record<string, string>>();
  keyByIdRef.current ??= {};

  useSyncEffectWithPrevDeps(() => {
    const keyById = keyByIdRef.current!;
    const oldIdByNewId = new Map<string, string>();
    for (const [oldId, newId] of Object.entries(activityIdReplacements ?? {})) {
      oldIdByNewId.set(newId, oldId);
    }

    for (const itemId of viewportIds) {
      if (keyById[itemId]) continue;

      let oldId = oldIdByNewId.get(itemId);
      const visitedIds = new Set<string>();
      while (oldId && !visitedIds.has(oldId)) {
        if (keyById[oldId]) {
          keyById[itemId] = keyById[oldId];
          break;
        }
        visitedIds.add(oldId);
        oldId = oldIdByNewId.get(oldId);
      }

      keyById[itemId] ??= generateUniqueId();
    }

    // Clean the memory
    const newActivityIds = new Set(viewportIds);
    for (const itemId of Object.keys(keyById)) {
      if (!newActivityIds.has(itemId)) {
        delete keyById[itemId];
      }
    }
  }, [viewportIds, activityIdReplacements]);

  return useLastCallback((itemId: string) => keyByIdRef.current?.[itemId] ?? itemId);
}

/**
 * In order to make the list look stationary after it has been scrolled, this hook compensates the scroll position when
 * new items are added and user scrolled content.
 */
function usePreventScrolledListShift(
  viewportIds: readonly string[],
  itemPositionById: Record<string, ItemPosition>,
  isPortrait: boolean,
  containerRef: React.RefObject<HTMLDivElement | undefined>,
  hasUserScrolledRef: React.RefObject<boolean>,
  scrollContainerSelector: string | undefined,
) {
  useLayoutEffectWithPrevDeps(([prevItemPositionById]) => {
    const container = scrollContainerSelector
      ? containerRef.current?.closest<HTMLElement>(scrollContainerSelector)
      : getScrollableContainer(containerRef.current, isPortrait);
    if (!hasUserScrolledRef.current || !container || !prevItemPositionById || !viewportIds.length) return;

    const anchorId = viewportIds[Math.floor(viewportIds.length / 2)];
    const prevTop = prevItemPositionById[anchorId]?.top;
    const nextTop = itemPositionById[anchorId]?.top;

    if (prevTop === undefined || nextTop === undefined || prevTop === nextTop) return;

    forceMeasure(() => {
      container.scrollBy({
        top: (nextTop - prevTop) * REM,
        behavior: 'instant',
      });
    });
  }, [itemPositionById, viewportIds, containerRef, hasUserScrolledRef, isPortrait, scrollContainerSelector]);
}
